import { useState, useEffect, useMemo } from 'react';
import { supabase } from '../supabaseClient';
import type { CurrentUser } from '../types/currentUser';
import type { ShowToast, ShowConfirm, FetchData } from '../types/appShared';

// ══════════════════════════════════════════════
// useTeamController — نفس فكرة باقي الـ controllers، لصفحة "Team" (اللي فيها
// كمان الـ Attendance/Leave/Performance/Achievements كلها مدمجة جوه نفس
// TeamView.jsx). زي CommentsView/PhotosView، جزء من الـ handlers (الموافقة/
// رفض الإجازات، حفظ درجة الأداء) موجودة جوّا TeamView.jsx نفسها مباشرة
// (بتستخدم currentProjectId/showToast/fetchData اللي بتتبعت ليها كـ props).
//
// اللي اتساب في App.jsx (مركزي):
// - teamData/setTeamData — بتتحمّل من fetchData ومُستخدمة في صفحات تانية كتير.
// - allTeamMembers/setAllTeamMembers + fetchAllTeamMembers — بيتحمّلوا من
//   نفس الـ effect اللي بيعمل fetchData عند تسجيل الدخول/تبديل مشروع (مش
//   بتتبعت لـ fetchData نفسها، effect مستقل).
// - attendanceData/setAttendanceData — بتتحمّل من fetchData ومُستخدمة في
//   OverviewView كمان.
// - leaveRequests/setLeaveRequests, performanceData/setPerformanceData —
//   بيتحمّلوا من fetchData، وperformanceData مُستخدمة كمان في تقرير PDF تاني
//   (مش بتاع صفحة Team بس).
// - canManageAttendance — permission helper عام هيتعامل معه مع باقي دوال
//   الصلاحيات في مرحلة Auth/routing.
//
// ✅ باج اكتشفته وقت النقل: fetchAchievements كانت بتتستدعى بس من جوه
// updateTeamMember (يعني allAchievements فاضية لحد ما حد يعدّل عضو فريق أول
// مرة في الجلسة — شارات/إنجازات الفريق ما كانت بتظهر من بداية تحميل الصفحة).
// ضفت useEffect هنا يحمّلها مرة واحدة عند أول mount (مش محتاجة project_id
// أصلاً، team_achievements مش مفلترة بمشروع).
//
// ملاحظة TS: PROJECT بتتبعت من App.jsx (لسه .jsx، من غير type رسمي)، فاتسابت
// هنا كـ any — زي currentUser اللي بيتبعت بس من غير ما يتستخدم فعليًا جوه
// الهوك ده (موجود كـ parameter بس من غير استخدام داخلي، زي ما كان في النسخة
// الأصلية بالظبط).
// ══════════════════════════════════════════════
export interface TeamMember {
    id: string | number;
    name: string;
    name_arabic?: string | null;
    position: string;
    employee_id?: string | null;
    department?: string | null;
    nationality?: string | null;
    blood_type?: string | null;
    phone?: string | null;
    email?: string | null;
    experience_years?: number | string | null;
    avatar?: string | null;
    skills?: string | null;
    status?: string;
    employment_type?: string;
    badge_issue_date?: string | null;
    badge_expiry_date?: string | null;
    assigned_systems?: string | null;
}

type EditingMember = TeamMember & { avatarFile?: File | null };

export interface Achievement {
    id: string | number;
    member_id: string | number;
    holder_name?: string;
    title: string;
    type: string;
    issuer?: string | null;
    year?: number | null;
    is_active?: boolean;
    sort_order?: number;
}

interface NewAchievementForm {
    title: string;
    type: string;
    issuer: string;
    year: number;
}

export interface AttendanceRow {
    id?: string | number;
    member_id: string | number;
    project_id?: string | null;
    date?: string;
    status: string;
    check_in?: string | null;
    check_out?: string | null;
    working_hours?: number;
    late_minutes?: number;
    notes?: string | null;
}

interface AttendanceRecordEntry {
    status?: string;
    check_in?: string | null;
    check_out?: string | null;
    late_minutes?: number;
    notes?: string | null;
}

interface NewMemberForm {
    name: string;
    name_arabic: string;
    position: string;
    employee_id: string;
    department: string;
    nationality: string;
    blood_type: string;
    email: string;
    phone: string;
    avatar: string | null;
    avatarFile?: File | null;
    experience_years?: string | number;
    skills: string;
    assigned_systems: string;
    employment_type: string;
    badge_issue_date: string;
    badge_expiry_date: string;
}

interface ExportRange {
    from: string;
    to: string;
}

interface UseTeamControllerParams {
    currentProjectId: string | null;
    currentUser: CurrentUser | null;
    PROJECT: any;
    showToast: ShowToast;
    showConfirm: ShowConfirm;
    fetchData: FetchData;
    fetchAllTeamMembers: () => Promise<void> | void;
    teamData: TeamMember[];
    attendanceData: AttendanceRow[];
}

export function useTeamController({
    currentProjectId, currentUser, PROJECT, showToast, showConfirm, fetchData, fetchAllTeamMembers,
    teamData, attendanceData,
}: UseTeamControllerParams) {
    const [editingMember, setEditingMember] = useState<EditingMember | null>(null);
    const [showEditMemberModal, setShowEditMemberModal] = useState(false);

    const [allAchievements, setAllAchievements] = useState<Achievement[]>([]);
    const [memberAchievements, setMemberAchievements] = useState<Achievement[]>([]);
    const [newAchievement, setNewAchievement] = useState<NewAchievementForm>({
        title: '', type: 'certification', issuer: '', year: new Date().getFullYear()
    });

    const fetchAchievements = async () => {
        const { data } = await supabase
            .from('team_achievements')
            .select('*')
            .order('sort_order');
        setAllAchievements(data || []);
    };

    // ✅ باج fetchAchievements (شرح فوق) — حمّلها مرة واحدة عند أول mount.
    useEffect(() => {
        fetchAchievements();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [teamSearchQuery, setTeamSearchQuery] = useState('');
    const [teamPage, setTeamPage] = useState(1);
    useEffect(() => { setTeamPage(1); }, [teamSearchQuery]);

    const [processingLeaveId, setProcessingLeaveId] = useState<string | number | null>(null); // منع نقر مزدوج على موافقة/رفض إجازة
    const [showLeaveRequests, setShowLeaveRequests] = useState(false);

    const [showQRModal, setShowQRModal] = useState(false);
    const [qrUrl, setQrUrl] = useState('');

    const [showAttendanceModal, setShowAttendanceModal] = useState(false);
    const [attendanceDate, setAttendanceDate] = useState(new Date().toISOString().split('T')[0]);
    const [attendanceRecords, setAttendanceRecords] = useState<Record<string, AttendanceRecordEntry>>({});
    const [showAttendanceSummary, setShowAttendanceSummary] = useState(false);

    const [showExportModal, setShowExportModal] = useState(false);
    const [exportRange, setExportRange] = useState<ExportRange>({
        from: new Date(new Date().setDate(1)).toISOString().split('T')[0], // أول الشهر
        to: new Date().toISOString().split('T')[0] // اليوم
    });

    const [showPerformanceModal, setShowPerformanceModal] = useState(false);
    const [editingPerformance, setEditingPerformance] = useState<unknown>(null);
    const [savingPerformance, setSavingPerformance] = useState(false); // منع نقر مزدوج على "Save Score"

    const [showAddTeamMemberModal, setShowAddTeamMemberModal] = useState(false);
    const [showAssignExistingMemberModal, setShowAssignExistingMemberModal] = useState(false);
    const [uploadingTeamMember, setUploadingTeamMember] = useState(false);
    const [assigningMember, setAssigningMember] = useState(false);
    const [memberSearchTerm, setMemberSearchTerm] = useState('');
    const [selectedMemberToAssign, setSelectedMemberToAssign] = useState<TeamMember | null>(null);
    const [assignmentSystems, setAssignmentSystems] = useState('');
    const [newMemberForm, setNewMemberForm] = useState<NewMemberForm>({
        name: '',
        name_arabic: '',
        position: '',
        employee_id: '',
        department: '',
        nationality: '',
        blood_type: '',
        email: '',
        phone: '',
        avatar: null,
        skills: '',
        assigned_systems: '',
        employment_type: 'direct',
        badge_issue_date: '',
        badge_expiry_date: '',
    });

    const attendanceStats = useMemo(() => ({
        present: Object.values(attendanceRecords).filter(r => r.status === 'Present').length,
        absent: Object.values(attendanceRecords).filter(r => r.status === 'Absent').length,
        late: Object.values(attendanceRecords).filter(r => r.status === 'Late').length,
        leave: Object.values(attendanceRecords).filter(r => r.status === 'Leave').length,
        sick: Object.values(attendanceRecords).filter(r => r.status === 'Sick').length,
        emergency: Object.values(attendanceRecords).filter(r => r.status === 'Emergency').length,
    }), [attendanceRecords]);

    const attendanceSummary = useMemo(() => {
        return teamData.map(member => {
            const memberRecords = attendanceData.filter(r => r.member_id === member.id);
            const present = memberRecords.filter(r => r.status === 'Present').length;
            const late = memberRecords.filter(r => r.status === 'Late').length;
            const absent = memberRecords.filter(r => r.status === 'Absent').length;
            const leave = memberRecords.filter(r => r.status === 'Leave').length;
            const sick = memberRecords.filter(r => r.status === 'Sick').length;
            const emergency = memberRecords.filter(r => r.status === 'Emergency').length;
            const totalDays = memberRecords.length;
            const totalHoursSum = memberRecords.reduce((sum, r) => sum + (r.working_hours || 0), 0);
            const attendanceRate = totalDays > 0
                ? Math.round(((present + late) / totalDays) * 100)
                : 0;

            return {
                member,
                present,
                late,
                absent,
                leave,
                sick,
                emergency,
                totalDays,
                totalHours: totalHoursSum.toFixed(1),
                attendanceRate,
            };
        });
    }, [teamData, attendanceData]);

    const totalHours = useMemo(() =>
        Object.values(attendanceRecords).reduce((sum, r) => {
            const h = r.check_in && r.check_out
                ? Math.max((new Date(`${attendanceDate}T${r.check_out}`).getTime() - new Date(`${attendanceDate}T${r.check_in}`).getTime()) / 3600000, 0)
                : 0;
            return sum + h;
        }, 0).toFixed(1)
        , [attendanceRecords, attendanceDate]);

    // Open edit modal + load member achievements
    const openEditMemberModal = (member: TeamMember) => {
        setEditingMember({ ...member, assigned_systems: member.assigned_systems || '', avatarFile: null });
        setMemberAchievements(allAchievements.filter(a => a.member_id === member.id));
        setNewAchievement({ title: '', type: 'certification', issuer: '', year: new Date().getFullYear() });
        setShowEditMemberModal(true);
    };

    const updateTeamMember = async () => {
        if (!editingMember) return;
        setUploadingTeamMember(true);
        try {
            let avatarUrl = editingMember.avatar || null;

            if (editingMember.avatarFile) {
                const ext = editingMember.avatarFile.name.split('.').pop();
                const path = `team/avatars/${editingMember.id}_${Date.now()}.${ext}`;

                const { error: uploadError } = await supabase.storage
                    .from('avatars')
                    .upload(path, editingMember.avatarFile, { upsert: true });

                if (uploadError) throw new Error('Upload failed: ' + uploadError.message);

                const { data: urlData } = supabase.storage
                    .from('avatars')
                    .getPublicUrl(path);

                avatarUrl = urlData.publicUrl;
            }

            const { error: updateError } = await supabase
                .from('team_members')
                .update({
                    name: editingMember.name,
                    name_arabic: editingMember.name_arabic || null,
                    position: editingMember.position,
                    employee_id: editingMember.employee_id || null,
                    department: editingMember.department || null,
                    nationality: editingMember.nationality || null,
                    blood_type: editingMember.blood_type || null,
                    phone: editingMember.phone || null,
                    email: editingMember.email || null,
                    experience_years: editingMember.experience_years
                        ? Number(editingMember.experience_years) : null,
                    avatar: avatarUrl,
                    skills: editingMember.skills || null,
                    status: editingMember.status || 'Active',
                    employment_type: editingMember.employment_type || 'direct',
                    badge_issue_date: editingMember.badge_issue_date || null,
                    badge_expiry_date: editingMember.badge_expiry_date || null,
                })
                .eq('id', editingMember.id);

            if (updateError) throw new Error('Update failed: ' + updateError.message);

            await supabase
                .from('project_team_assignments')
                .update({ assigned_systems: editingMember.assigned_systems || null })
                .eq('team_member_id', editingMember.id)
                .eq('project_id', currentProjectId);

            showToast('✅ Member updated!');
            setShowEditMemberModal(false);
            setEditingMember(null);
            fetchData(currentProjectId);
            fetchAchievements();

        } catch (err: any) {
            console.error(err);
            showToast('❌ Failed to save team member. Please try again.');
        } finally {
            setUploadingTeamMember(false);
        }
    };

    const addMemberAchievement = async () => {
        if (!newAchievement.title || !editingMember?.id) return;

        const { data, error } = await supabase
            .from('team_achievements')
            .insert([{
                member_id: editingMember.id,
                holder_name: editingMember.name,
                title: newAchievement.title,
                type: newAchievement.type,
                issuer: newAchievement.issuer || null,
                year: newAchievement.year || null,
                is_active: true,
            }])
            .select()
            .single();

        if (!error && data) {
            setMemberAchievements(prev => [...prev, data]);
            setAllAchievements(prev => [...prev, data]);
            setNewAchievement({ title: '', type: 'certification', issuer: '', year: new Date().getFullYear() });
            showToast('✅ Achievement added!');
        }
    };

    const removeMemberAchievement = async (achievementId: string | number) => {
        await supabase.from('team_achievements').delete().eq('id', achievementId);
        setMemberAchievements(prev => prev.filter(a => a.id !== achievementId));
        setAllAchievements(prev => prev.filter(a => a.id !== achievementId));
        showToast('🗑️ Removed');
    };

    const saveAttendance = async () => {
        try {
            const records = teamData
                .filter(member => {
                    const rec = attendanceRecords[member.id] || {};
                    return rec.check_in ||
                        rec.status === 'Absent' ||
                        rec.status === 'Late' ||
                        rec.status === 'Leave' ||
                        rec.status === 'Sick' ||
                        rec.status === 'Emergency';
                })
                .map(member => {
                    const rec = attendanceRecords[member.id] || {};
                    const checkIn = rec.check_in || null;
                    const checkOut = rec.check_out || null;
                    const workingHours = checkIn && checkOut
                        ? Math.round(((new Date(`2000-01-01T${checkOut}`).getTime() - new Date(`2000-01-01T${checkIn}`).getTime()) / 3600000) * 100) / 100
                        : 0;
                    return {
                        member_id: member.id,
                        project_id: currentProjectId,
                        date: attendanceDate,
                        status: rec.status || 'Absent',
                        check_in: checkIn,
                        check_out: checkOut,
                        working_hours: workingHours,
                        late_minutes: rec.late_minutes || 0,
                        notes: rec.notes || null,
                    };
                });

            if (records.length > 0) {
                const { error } = await supabase
                    .from('attendance')
                    .upsert(records, { onConflict: 'member_id,project_id,date' });

                if (error) throw error;
            }

            showToast('✅ Attendance saved!');
            setShowAttendanceModal(false);
            fetchData(currentProjectId);
        } catch (err: any) {
            console.error('Attendance save error:', err);
            showToast('❌ Failed to save attendance. Please try again.');
        }
    };

    const openAttendanceModal = async (date?: string) => {
        const d = date || new Date().toISOString().split('T')[0];
        setAttendanceDate(d);

        const { data } = await supabase
            .from('attendance')
            .select('*')
            .eq('project_id', currentProjectId)
            .eq('date', d);

        const mapped: Record<string, AttendanceRecordEntry> = {};
        (data || []).forEach((r: AttendanceRow) => {
            mapped[String(r.member_id)] = r;
        });

        teamData.forEach(m => {
            if (!mapped[String(m.id)]) {
                mapped[String(m.id)] = {
                    status: 'Absent',
                    check_in: PROJECT?.work_start?.slice(0, 5) || '07:30',
                    check_out: PROJECT?.work_end?.slice(0, 5) || '17:30',
                    late_minutes: 0,
                    notes: ''
                };
            }
        });

        setAttendanceRecords(mapped);
        setShowAttendanceModal(true);
    };

    const generateAttendanceQR = () => {
        const url = `${window.location.origin}/attendance.html?project=${currentProjectId}`;
        setQrUrl(url);
        setShowQRModal(true);
    };

    const exportAttendanceRange = async () => {
        const { data } = await supabase
            .from('attendance')
            .select('*')
            .eq('project_id', currentProjectId)
            .gte('date', exportRange.from)
            .lte('date', exportRange.to)
            .order('date', { ascending: true });

        if (!data || data.length === 0) {
            showToast('No attendance data in this range');
            return;
        }

        const rows = data.map((rec: AttendanceRow) => {
            const member = teamData.find(m => m.id === rec.member_id);
            return {
                'Date': rec.date,
                'Name': member?.name || '-',
                'Position': member?.position || '-',
                'Status': rec.status,
                'Check In': rec.check_in || '-',
                'Check Out': rec.check_out || '-',
                'Hours': rec.working_hours || '-',
                'Late (min)': rec.late_minutes || 0,
                'Notes': rec.notes || '',
            };
        });

        const headers = Object.keys(rows[0]) as (keyof typeof rows[0])[];
        const csvContent = [
            headers.join(','),
            ...rows.map(row => headers.map(h => `"${row[h]}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Attendance_${PROJECT?.short_name}_${exportRange.from}_to_${exportRange.to}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        showToast('✅ Exported!');
        setShowExportModal(false);
    };

    const generateAttendancePDF = async () => {
        const { jsPDF } = (window as any).jspdf || await import('jspdf');
        const autoTable = (await import('jspdf-autotable')).default;

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

        const margin = 15;
        let y = 20;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(16);
        doc.text(
            `Attendance Summary — ${new Date().toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}`,
            margin, y
        );

        y += 10;

        const totalMembers = teamData.length;
        const avgAttendance = Math.round(attendanceSummary.reduce((s, m) => s + m.attendanceRate, 0) / (attendanceSummary.length || 1));
        const totalAbsent = attendanceSummary.reduce((s, m) => s + m.absent, 0);
        const totalLate = attendanceSummary.reduce((s, m) => s + m.late, 0);
        const totalSick = attendanceSummary.reduce((s, m) => s + m.sick, 0);
        const totalEmergency = attendanceSummary.reduce((s, m) => s + m.emergency, 0);

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        doc.text(`Total Members: ${totalMembers}`, margin, y);
        doc.text(`Avg Attendance: ${avgAttendance}%`, margin + 60, y);
        doc.text(`Total Absent: ${totalAbsent}`, margin + 120, y);

        y += 6;

        doc.text(`Total Late: ${totalLate}`, margin, y);
        doc.text(`Total Sick: ${totalSick}`, margin + 60, y);
        doc.text(`Total Emergency: ${totalEmergency}`, margin + 120, y);

        y += 10;

        autoTable(doc, {
            startY: y,
            head: [['#', 'Member', 'Position', 'Present', 'Late', 'Absent', 'Leave', 'Sick', 'Emergency', 'Hours', 'Rate%']],
            body: attendanceSummary.map((row, i) => [
                i + 1,
                row.member.name,
                row.member.position,
                row.present,
                row.late,
                row.absent,
                row.leave,
                row.sick,
                row.emergency,
                `${row.totalHours}h`,
                `${row.attendanceRate}%`,
            ]),
            styles: { fontSize: 8, cellPadding: 3 },
            headStyles: {
                fillColor: [37, 99, 235],
                textColor: [255, 255, 255],
                fontStyle: 'bold',
            },
            alternateRowStyles: { fillColor: [248, 250, 252] },
            didParseCell: (data: any) => {
                if (data.section === 'body') {
                    if (data.column.index === 10) {
                        const val = parseInt(data.cell.raw);
                        data.cell.styles.textColor =
                            val >= 80 ? [16, 185, 129] :
                                val >= 60 ? [245, 158, 11] :
                                    [239, 68, 68];
                        data.cell.styles.fontStyle = 'bold';
                        data.cell.styles.halign = 'center';
                    }
                    if (data.column.index === 7 && parseInt(data.cell.raw) > 0) {
                        data.cell.styles.textColor = [168, 85, 247];
                        data.cell.styles.fontStyle = 'bold';
                    }
                    if (data.column.index === 8 && parseInt(data.cell.raw) > 0) {
                        data.cell.styles.textColor = [249, 115, 22];
                        data.cell.styles.fontStyle = 'bold';
                    }
                }
            },
            margin: { left: margin, right: margin },
        });

        doc.save(`Attendance_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.pdf`);
    };

    const addNewTeamMember = async () => {
        if (!newMemberForm.name || !newMemberForm.position) {
            showToast('Please fill all required fields');
            return;
        }

        setUploadingTeamMember(true);

        try {
            let avatarUrl = null;

            if (newMemberForm.avatarFile) {
                const fileExt = newMemberForm.avatarFile.name.split('.').pop();
                const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
                const filePath = `team/avatars/${fileName}`;

                const { error: uploadError } = await supabase.storage
                    .from('avatars')
                    .upload(filePath, newMemberForm.avatarFile, {
                        cacheControl: '3600',
                        upsert: false
                    });

                if (!uploadError) {
                    const { data: { publicUrl } } = supabase.storage
                        .from('avatars')
                        .getPublicUrl(filePath);
                    avatarUrl = publicUrl;
                }
            }

            const { data: memberData, error: memberError } = await supabase
                .from('team_members')
                .insert([{
                    name: newMemberForm.name,
                    name_arabic: newMemberForm.name_arabic || null,
                    position: newMemberForm.position,
                    employee_id: newMemberForm.employee_id || null,
                    department: newMemberForm.department || null,
                    phone: newMemberForm.phone || null,
                    email: newMemberForm.email || null,
                    nationality: newMemberForm.nationality || null,
                    blood_type: newMemberForm.blood_type || null,
                    experience_years: newMemberForm.experience_years ? Number(newMemberForm.experience_years) : null,
                    avatar: avatarUrl,
                    skills: newMemberForm.skills || null,
                    status: 'Active',
                    employment_type: newMemberForm.employment_type || 'direct',
                    badge_issue_date: newMemberForm.badge_issue_date || null,
                    badge_expiry_date: newMemberForm.badge_expiry_date || null,
                }])
                .select()
                .single();

            if (memberError) throw memberError;

            const { error: assignError } = await supabase
                .from('project_team_assignments')
                .insert([{
                    project_id: currentProjectId,
                    team_member_id: memberData.id,
                    assigned_role: newMemberForm.position,
                    assigned_systems: newMemberForm.assigned_systems || null,
                    performance_score: 0,
                }]);

            if (assignError) throw assignError;

            showToast('Team member added successfully!');
            setShowAddTeamMemberModal(false);
            setNewMemberForm({
                name: '', name_arabic: '', position: '', employee_id: '', department: '', nationality: '',
                blood_type: '', email: '', phone: '', experience_years: '',
                avatar: '', avatarFile: null,
                skills: '', assigned_systems: '', employment_type: 'direct',
                badge_issue_date: '', badge_expiry_date: '',
            });

            fetchData();
            fetchAllTeamMembers();

        } catch (err: any) {
            console.error('Error adding team member:', err);
            showToast('❌ Failed to add team member. Please try again.');
        } finally {
            setUploadingTeamMember(false);
        }
    };

    const assignExistingMember = async () => {
        if (!selectedMemberToAssign) {
            showToast('Please select a member to assign');
            return;
        }

        setAssigningMember(true);

        try {
            const { error } = await supabase
                .from('project_team_assignments')
                .insert([{
                    project_id: currentProjectId,
                    team_member_id: selectedMemberToAssign.id,
                    assigned_role: selectedMemberToAssign.position,
                    assigned_systems: assignmentSystems || null,
                    performance_score: 0,
                }]);

            if (error) throw error;

            showToast(`${selectedMemberToAssign.name} assigned to project successfully!`);
            setShowAssignExistingMemberModal(false);
            setSelectedMemberToAssign(null);
            setAssignmentSystems('');
            setMemberSearchTerm('');
            fetchData();

        } catch (err: any) {
            console.error('Error assigning member:', err);
            showToast('❌ Failed to assign member. Please try again.');
        } finally {
            setAssigningMember(false);
        }
    };

    const removeTeamMember = async (memberId: string | number) => {
        try {
            const { error } = await supabase
                .from('project_team_assignments')
                .delete()
                .eq('project_id', currentProjectId)
                .eq('team_member_id', memberId);

            if (error) throw error;

            await supabase
                .from('performance')
                .delete()
                .eq('member_id', memberId)
                .eq('project_id', currentProjectId);

            showToast('Member removed from project');
            fetchData(currentProjectId);

        } catch (err: any) {
            console.error('Error removing member:', err);
            showToast('❌ Failed to remove member. Please try again.');
        }
    };

    return {
        editingMember, setEditingMember, showEditMemberModal, setShowEditMemberModal, openEditMemberModal, updateTeamMember,
        showAddTeamMemberModal, setShowAddTeamMemberModal, newMemberForm, setNewMemberForm, addNewTeamMember, uploadingTeamMember,
        showAssignExistingMemberModal, setShowAssignExistingMemberModal, memberSearchTerm, setMemberSearchTerm,
        selectedMemberToAssign, setSelectedMemberToAssign, assignmentSystems, setAssignmentSystems, assigningMember, assignExistingMember,
        removeTeamMember,
        allAchievements, memberAchievements, newAchievement, setNewAchievement, addMemberAchievement, removeMemberAchievement,
        showQRModal, setShowQRModal, qrUrl, generateAttendanceQR,
        showAttendanceModal, setShowAttendanceModal, attendanceDate, attendanceRecords, setAttendanceRecords,
        openAttendanceModal, saveAttendance,
        showAttendanceSummary, setShowAttendanceSummary, attendanceStats, attendanceSummary, totalHours,
        showExportModal, setShowExportModal, exportRange, setExportRange, exportAttendanceRange, generateAttendancePDF,
        showLeaveRequests, setShowLeaveRequests, processingLeaveId, setProcessingLeaveId,
        showPerformanceModal, setShowPerformanceModal, editingPerformance, setEditingPerformance, savingPerformance, setSavingPerformance,
        teamSearchQuery, setTeamSearchQuery, teamPage, setTeamPage,
    };
}

export default useTeamController;
